import * as THREE from 'three';

/**
 * Stylising filters over the finished world picture: pixelation, palettes and dithering, ink outlines, a painterly smear, paper grain,
 * a colour grade and an old screen. Development only for now: the scenario viewer's `picture-filters` scenario edits `PICTURE_FILTERS`.
 * When every value is neutral the game scene draws straight to the canvas as before, so the filters cost nothing while off.
 */
export interface PictureFilters {
  /** Screen pixels per picture pixel; 1 is off. The world is drawn this much smaller and scaled up, so pixelation is cheaper, not dearer. */
  pixelSize: number;
  /** Colours per channel (0 off). Ignored while a palette is chosen. */
  posterize: number;
  /** A fixed palette every pixel snaps to; '' is off. */
  palette: '' | PaletteId;
  /** Ordered (Bayer) dithering between palette or posterize steps, 0–1. */
  dither: number;
  /** Ink outlines from depth and colour edges, 0–1, their thickness in picture pixels, and how small a depth step still inks (metres). */
  ink: number; inkThickness: number; inkDepth: number;
  /** Painterly (Kuwahara) brush radius in picture pixels; 0 is off. */
  painterly: number;
  /** Paper grain and fibres pinned to the world (they hold still as the camera pans), 0–1. */
  grain: number;
  /** Colour grade: warm (+) or cool (−), saturation and contrast (1 untouched), darkened corners. */
  warmth: number; saturation: number; contrast: number; vignette: number;
  /** Old screen: scanlines with an aperture mask, barrel curvature, colour fringing. */
  scanlines: number; curvature: number; aberration: number;
  /** Tilt-shift: blur toward the top and bottom of the screen (a miniature look), and blur toward its corners, 0–1. */
  tilt: number; edgeFade: number;
  /** Light-aware grade: warm in the lit parts, cool in the shadows, 0–1. */
  splitTone: number;
  /** Ink that thickens where the depth step is large (figures, trees) and stays thin on grass and stones, 0–1. */
  inkWeight: number;
  /** How far the grade follows the scenario's clock (amber at dawn and dusk, slate at night), 0–1. */
  timeGrade: number;
  /** Split screen: the picture before the filters (at the pixel size) left of `compareAt`, the filtered picture right of it. */
  compare: boolean; compareAt: number;
  /** Where the world's 3D HUD layer goes: over the filtered picture (`crisp`), into it (`filtered`), or over it with the page's HUD tinted to match by CSS (`graded`). */
  hud: HudMode;
  /** Whether the ground UI (rings, the selection glyph, order marks, pings) is filtered with the world or drawn sharp over it. */
  groundUi: 'filtered' | 'crisp';
  /** The order the colour stages run in. */
  order: StageId[];
  /** Which of these scale with the camera zoom (reference zoom 1) instead of staying fixed on the screen. For `grain` it means pinned to the world. */
  follows: ZoomKey[];
}

/** The filters that can follow the zoom. Pixel size steps by half pixels; ink thickness and brush are whole pixels, so they step rather than blur. */
export const ZOOM_KEYS = ['pixelSize', 'inkThickness', 'painterly', 'grain'] as const;
export type ZoomKey = typeof ZOOM_KEYS[number];

export type HudMode = 'crisp' | 'graded' | 'filtered';
export const STAGES = ['ink', 'grade', 'grain', 'vignette', 'palette', 'scanlines'] as const;
export type StageId = typeof STAGES[number];
export const DEFAULT_ORDER: readonly StageId[] = STAGES;

export const NEUTRAL_FILTERS: Readonly<PictureFilters> = {
  pixelSize: 1, posterize: 0, palette: '', dither: 0, ink: 0, inkThickness: 1, inkDepth: .35, painterly: 0, grain: 0,
  warmth: 0, saturation: 1, contrast: 1, vignette: 0, scanlines: 0, curvature: 0, aberration: 0,
  tilt: 0, edgeFade: 0, splitTone: 0, inkWeight: 0, timeGrade: 0, compare: false, compareAt: .5, hud: 'crisp', groundUi: 'filtered', order: [...DEFAULT_ORDER], follows: ['grain'],
};

/** Values that change the picture only alongside another filter; on their own they leave it untouched. */
const MODIFIERS = new Set<keyof PictureFilters>(['dither', 'inkThickness', 'inkDepth', 'inkWeight', 'compareAt', 'hud', 'groundUi', 'order', 'follows']);

export const PICTURE_FILTERS: PictureFilters = { ...NEUTRAL_FILTERS, order: [...DEFAULT_ORDER], follows: [...NEUTRAL_FILTERS.follows] };

/**
 * The game's own picture style, chosen in the scenario viewer's filter panel: a fine dithered pixel grid and a little brush, ink on the edges of
 * things, paper grain, warm lights over cool shadows and crisp ground marks. Everything it leaves out keeps its neutral value.
 */
export const GAME_LOOK: Readonly<Partial<PictureFilters>> = {
  pixelSize: 2, posterize: 24, dither: 1, ink: .3, inkDepth: 1.3, painterly: 1, grain: .37, contrast: 1.15, splitTone: 1, groundUi: 'crisp',
  order: ['grade', 'grain', 'ink', 'scanlines', 'palette', 'vignette'], follows: ['pixelSize', 'inkThickness', 'painterly', 'grain'],
};

/** Sets the live filters to the game's picture style, or to nothing (the plain picture, drawn straight to the canvas) when it is off. */
export function applyGameLook(on: boolean) {
  const look = on ? GAME_LOOK : {};
  Object.assign(PICTURE_FILTERS, NEUTRAL_FILTERS, look, { order: [...(look.order ?? DEFAULT_ORDER)], follows: [...(look.follows ?? NEUTRAL_FILTERS.follows)] });
}

/** sRGB palettes drawn from the art direction's earth, moss, slate, bone and ember. */
export const PALETTES = {
  earth: { label: 'Earth 16', colors: [0x16110d, 0x3b2a1e, 0x5c4030, 0x8a6244, 0xb98d5e, 0xdcc28f, 0xf0e2bd, 0x252d1f, 0x4a5a32, 0x7a8a48, 0x262f3a, 0x4f6070, 0x8a9aa4, 0x6e2a1e, 0xb8602a, 0xd9a040] },
  ember: { label: 'Ember 9', colors: [0x140c0a, 0x3a2014, 0x6e3a1c, 0xa8642c, 0xd8a050, 0xf4dca0, 0x2c3a44, 0x3e4a28, 0x76803e] },
  slate: { label: 'Slate night 8', colors: [0x0e1016, 0x1e2632, 0x34445a, 0x58708a, 0x8ca4b4, 0xc8d0c8, 0x6a4a2a, 0xc89050] },
  moss: { label: 'Moss 4', colors: [0x1c2214, 0x3e4e26, 0x7a8a46, 0xc4c890] },
} as const;
export type PaletteId = keyof typeof PALETTES;
const MAX_COLORS = 16;

export function pictureFiltersActive(filters: Readonly<PictureFilters> = PICTURE_FILTERS) {
  for (const key in NEUTRAL_FILTERS) {
    const name = key as keyof PictureFilters;
    if (!MODIFIERS.has(name) && filters[name] !== NEUTRAL_FILTERS[name]) return true;
  }
  return false;
}

/** The scenario's clock in minutes, fed by the panel for `timeGrade`. */
export const pictureFilterClock = { minutes: 12 * 60 };

/** Grade through the day: hour, warmth, saturation, contrast, vignette, split tone. Amber at the edges of the day, slate at night. */
const DAY_GRADE: readonly (readonly number[])[] = [
  [0, -.55, .65, 1.05, .4, .6], [5, -.3, .8, 1.05, .3, .5], [6.5, .75, .9, 1, .3, .5], [9, .2, 1, 1, 0, .2], [13, 0, 1, 1, 0, 0],
  [17, .4, .95, 1, .15, .3], [19, .85, .85, 1.05, .3, .5], [21, -.2, .7, 1.05, .4, .6], [24, -.55, .65, 1.05, .4, .6],
];
function dayGrade(minutes: number) {
  const hour = minutes / 60 % 24;
  let i = 1; while (i < DAY_GRADE.length - 1 && DAY_GRADE[i][0] < hour) i++;
  const a = DAY_GRADE[i - 1], b = DAY_GRADE[i], t = (hour - a[0]) / (b[0] - a[0]);
  return a.map((value, k) => value + (b[k] - value) * t);
}
/** The filters with the day's grade mixed in at `timeGrade`. */
export function withClock(filters: Readonly<PictureFilters>): Readonly<PictureFilters> {
  if (filters.timeGrade <= 0) return filters;
  const [, warmth, saturation, contrast, vignette, splitTone] = dayGrade(pictureFilterClock.minutes), k = filters.timeGrade;
  return { ...filters, warmth: filters.warmth + warmth * k, saturation: filters.saturation * (1 + (saturation - 1) * k), contrast: filters.contrast * (1 + (contrast - 1) * k),
    vignette: Math.min(1, filters.vignette + vignette * k), splitTone: Math.min(1, filters.splitTone + splitTone * k) };
}
/** The filters with the zoom-following values scaled to the camera's zoom. */
function withZoom(filters: Readonly<PictureFilters>, zoom: number): Readonly<PictureFilters> {
  const follows = filters.follows;
  if (!follows.some(key => key !== 'grain')) return filters;
  const scaled = { ...filters }, base = Math.max(1, filters.pixelSize);
  // Pixels follow the zoom in half-pixel steps, and never fall below two: zoomed out far enough they would stop being pixels at all.
  if (follows.includes('pixelSize') && filters.pixelSize > 1) scaled.pixelSize = Math.min(16, Math.max(2, Math.round(filters.pixelSize * zoom * 2) / 2));
  // Ink and brush are set in picture pixels of the dial's own pixel size. Their size on the screen is that times the zoom when they follow it,
  // and the picture pixel itself may already have followed: divide by the pixel actually drawn, or the two scalings would multiply.
  const inPictureShare = (value: number, follow: boolean, most: number) => Math.min(most, Math.max(1, value * base * (follow ? zoom : 1) / Math.max(1, scaled.pixelSize)));
  // Ink keeps its fraction (the shader blends the two nearest whole widths, so the line grows smoothly with the zoom); the brush radius is a whole number.
  scaled.inkThickness = inPictureShare(filters.inkThickness, follows.includes('inkThickness'), 8);
  if (filters.painterly >= 1) scaled.painterly = Math.round(inPictureShare(filters.painterly, follows.includes('painterly'), 4));
  return scaled;
}
/** The CSS filter that tints the page's own HUD toward the picture's grade (`hud` other than `crisp`), or ''. */
export function hudCssFilter(filters: Readonly<PictureFilters> = PICTURE_FILTERS) {
  if (filters.hud === 'crisp' || !pictureFiltersActive(filters)) return '';
  const f = withClock(filters), parts: string[] = [];
  if (f.saturation !== 1) parts.push(`saturate(${f.saturation.toFixed(2)})`);
  if (f.contrast !== 1) parts.push(`contrast(${f.contrast.toFixed(2)})`);
  if (f.warmth > 0) parts.push(`sepia(${Math.min(.6, f.warmth * .35).toFixed(2)})`);
  else if (f.warmth < 0) parts.push(`hue-rotate(${(f.warmth * 14).toFixed(0)}deg)`);
  return parts.join(' ');
}

/** The last measured cost of the filter pass, for the panel. `gpuMs` is null where the browser has no GPU timer. */
export const pictureFilterStats = { gpuMs: null as number | null, width: 0, height: 0 };

const vertexShader = /* glsl */`
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }`;

const fragmentShader = /* glsl */`
#include <packing>
uniform sampler2D tColor; uniform sampler2D tDepth;
uniform vec2 size; uniform float cameraNear, cameraFar; uniform mat4 inverseViewProjection;
uniform float posterize, dither, ink, inkThickness, inkDepth, painterly, grain;
uniform float warmth, saturation, contrast, vignette, scanlines, curvature, aberration, tilt, edgeFade, splitTone, inkWeight, compareAt;
uniform bool compare, grainWorld; uniform int stageOrder[6];
uniform bool antialias; uniform int paletteSize; uniform vec3 palette[${MAX_COLORS}]; uniform vec3 inkColor;
varying vec2 vUv;

const float BAYER[16] = float[16](0., 8., 2., 10., 12., 4., 14., 6., 3., 11., 1., 9., 15., 7., 13., 5.);
float bayer(vec2 cell) { ivec2 i = ivec2(mod(cell, 4.)); return (BAYER[i.x + i.y * 4] + .5) / 16.; }
float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
  return mix(mix(hash(i), hash(i + vec2(1., 0.)), f.x), mix(hash(i + vec2(0., 1.)), hash(i + 1.), f.x), f.y);
}
float luma(vec3 c) { return dot(c, vec3(.2126, .7152, .0722)); }
float hash3(vec3 p) { p = fract(p * .1031); p += dot(p, p.zyx + 31.32); return fract((p.x + p.y) * p.z); }
float noise3(vec3 p) {
  vec3 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
  return mix(mix(mix(hash3(i), hash3(i + vec3(1., 0., 0.)), f.x), mix(hash3(i + vec3(0., 1., 0.)), hash3(i + vec3(1., 1., 0.)), f.x), f.y),
    mix(mix(hash3(i + vec3(0., 0., 1.)), hash3(i + vec3(1., 0., 1.)), f.x), mix(hash3(i + vec3(0., 1., 1.)), hash3(i + vec3(1., 1., 1.)), f.x), f.y), f.z);
}
// The world position under a pixel, from its depth: paper grain is pinned to it, so it stays put as the camera pans and grows as it zooms.
vec3 worldAt(vec2 uv) {
  vec4 w = inverseViewProjection * vec4(uv * 2. - 1., texture2D(tDepth, uv).x * 2. - 1., 1.);
  return w.xyz / w.w;
}
float viewZ(vec2 uv) { return orthographicDepthToViewZ(texture2D(tDepth, uv).x, cameraNear, cameraFar); }

// Generalised four-quadrant Kuwahara: each pixel takes the mean of its calmest neighbouring square, so flat areas become brush strokes.
vec3 kuwahara(vec2 uv, int r) {
  ivec2 center = ivec2(uv * size);
  vec3 m[4] = vec3[4](vec3(0.), vec3(0.), vec3(0.), vec3(0.)), s[4] = vec3[4](vec3(0.), vec3(0.), vec3(0.), vec3(0.));
  for (int j = -4; j <= 4; j++) for (int i = -4; i <= 4; i++) {
    if (abs(i) > r || abs(j) > r) continue;
    vec3 c = texelFetch(tColor, clamp(center + ivec2(i, j), ivec2(0), ivec2(size) - 1), 0).rgb; 
    vec3 cc = c * c;
    if (i <= 0 && j <= 0) { m[0] += c; s[0] += cc; }
    if (i >= 0 && j <= 0) { m[1] += c; s[1] += cc; }
    if (i <= 0 && j >= 0) { m[2] += c; s[2] += cc; }
    if (i >= 0 && j >= 0) { m[3] += c; s[3] += cc; }
  }
  float n = float((r + 1) * (r + 1)), best = 1e9; vec3 result = vec3(0.);
  for (int k = 0; k < 4; k++) {
    vec3 mean = m[k] / n, variance = abs(s[k] / n - mean * mean);
    float v = variance.r + variance.g + variance.b;
    if (v < best) { best = v; result = mean; }
  }
  return result;
}

// FXAA (the console variant): blurs along a stair-stepped edge, standing in for the canvas's multisampling.
vec3 fxaa(vec2 uv) {
  vec2 px = 1. / size;
  vec3 m = texture2D(tColor, uv).rgb, nw = texture2D(tColor, uv + vec2(-.5, -.5) * px).rgb, ne = texture2D(tColor, uv + vec2(.5, -.5) * px).rgb,
    sw = texture2D(tColor, uv + vec2(-.5, .5) * px).rgb, se = texture2D(tColor, uv + vec2(.5, .5) * px).rgb;
  float lM = luma(m), lNW = luma(nw), lNE = luma(ne), lSW = luma(sw), lSE = luma(se);
  float lMin = min(lM, min(min(lNW, lNE), min(lSW, lSE))), lMax = max(lM, max(max(lNW, lNE), max(lSW, lSE)));
  if (lMax - lMin < max(.03, lMax * .12)) return m;
  vec2 dir = vec2(-((lNW + lNE) - (lSW + lSE)), (lNW + lSW) - (lNE + lSE));
  float reduce = max((lNW + lNE + lSW + lSE) * .03125, 1. / 128.);
  dir = clamp(dir / (min(abs(dir.x), abs(dir.y)) + reduce), -8., 8.) * px;
  vec3 a = .5 * (texture2D(tColor, uv + dir * (1. / 3. - .5)).rgb + texture2D(tColor, uv + dir * (2. / 3. - .5)).rgb);
  vec3 b = a * .5 + .25 * (texture2D(tColor, uv - dir * .5).rgb + texture2D(tColor, uv + dir * .5).rgb);
  float lB = luma(b);
  return lB < lMin || lB > lMax ? a : b;
}

float depthAtPixel(ivec2 p) { return orthographicDepthToViewZ(texelFetch(tDepth, clamp(p, ivec2(0), ivec2(size) - 1), 0).x, cameraNear, cameraFar); }
float depthStep(ivec2 p, int w) {
  // Only the nearer side of a depth step counts, so a figure is outlined on its own edge, not on the ground behind it.
  // Whole-pixel offsets keep a thick line as sharp as a thin one.
  return 4. * depthAtPixel(p) - (depthAtPixel(p + ivec2(w, 0)) + depthAtPixel(p - ivec2(w, 0)) + depthAtPixel(p + ivec2(0, w)) + depthAtPixel(p - ivec2(0, w)));
}

float edgeAt(vec2 uv, int w) {
  ivec2 p = ivec2(uv * size);
  float depthEdge = smoothstep(inkDepth * .8, inkDepth, depthStep(p, w));
  // Tall things (a figure, a tree) step far in depth: they get a second, wider line; grass and stones keep the thin one.
  if (inkWeight > 0.) depthEdge = max(depthEdge, inkWeight * smoothstep(inkDepth * 3., inkDepth * 3.6, depthStep(p, int(float(w) * 2.5 + .5))));
  // Colour edges use the same whole-pixel width, so a line is one weight wherever it comes from.
  float l = luma(texelFetch(tColor, clamp(p, ivec2(0), ivec2(size) - 1), 0).rgb), lAround = 0.;
  for (int k = 0; k < 4; k++) {
    ivec2 o = (k == 0 ? ivec2(-1, 0) : k == 1 ? ivec2(1, 0) : k == 2 ? ivec2(0, -1) : ivec2(0, 1)) * w;
    lAround += luma(texelFetch(tColor, clamp(p + o, ivec2(0), ivec2(size) - 1), 0).rgb);
  }
  float colorEdge = smoothstep(.1, .16, abs(4. * l - lAround));
  return max(depthEdge, colorEdge * .55);
}

// Lines are whole pixels, so they stay sharp; a fractional width blends the two nearest whole ones, so a line grows smoothly with the zoom.
float edge(vec2 uv) {
  float width = max(1., inkThickness);
  int w = int(floor(width));
  float fraction = width - float(w), thin = edgeAt(uv, w);
  return fraction > .02 ? mix(thin, edgeAt(uv, w + 1), fraction) : thin;
}

vec3 nearest(vec3 c) {
  vec3 best = palette[0]; float distance = 1e9;
  for (int i = 0; i < ${MAX_COLORS}; i++) {
    if (i >= paletteSize) break;
    vec3 d = (c - palette[i]) * vec3(.3, .59, .11) * 3.;
    float e = dot(d, d) + dot(c - palette[i], c - palette[i]) * .25;
    if (e < distance) { distance = e; best = palette[i]; }
  }
  return best;
}

// A twelve-tap disc blur, for the tilt-shift and the soft corners.
vec3 blurred(vec2 uv, float radius) {
  vec3 sum = texture2D(tColor, uv).rgb;
  for (int i = 0; i < 12; i++) {
    float a = float(i) * 2.399963, r = sqrt((float(i) + .5) / 12.) * radius;
    sum += texture2D(tColor, uv + vec2(cos(a), sin(a)) * r / size).rgb;
  }
  return sum / 13.;
}

void main() {
  vec2 uv = vUv;
  if (compare) {
    float edgeWidth = fwidth(vUv.x) * 1.5;
    if (abs(vUv.x - compareAt) < edgeWidth) { gl_FragColor = vec4(.93, .88, .75, 1.); return; }
    if (vUv.x < compareAt) { gl_FragColor = vec4(clamp(texture2D(tColor, vUv).rgb, 0., 1.), 1.); return; }
  }
  if (curvature > 0.) {
    vec2 c = uv * 2. - 1.; c *= 1. + curvature * .18 * dot(c, c); uv = c * .5 + .5;
    if (any(lessThan(uv, vec2(0.))) || any(greaterThan(uv, vec2(1.)))) { gl_FragColor = vec4(0., 0., 0., 1.); return; }
  }
  vec3 color = painterly > .5 ? kuwahara(uv, int(painterly + .5)) : antialias ? fxaa(uv) : texture2D(tColor, uv).rgb;
  if (aberration > 0.) {
    vec2 d = (uv - .5) * aberration * .012;
    color.r = texture2D(tColor, uv + d).r; color.b = texture2D(tColor, uv - d).b;
  }
  if (tilt > 0. || edgeFade > 0.) {
    vec2 c = uv * 2. - 1.;
    float radius = (tilt * smoothstep(.15, .85, abs(c.y)) + edgeFade * smoothstep(.5, 1.3, length(c))) * 7.;
    if (radius > .4) color = mix(color, blurred(uv, radius), min(1., radius));
  }
  vec2 cell = floor(uv * size);
  float threshold = bayer(cell) - .5;
  // The colour stages run in the order the panel sets.
  for (int k = 0; k < 6; k++) {
    int stage = stageOrder[k];
    if (stage == 0) {
      if (ink > 0.) color = mix(color, inkColor, edge(uv) * ink);
    } else if (stage == 1) {
      float l = luma(color);
      color = mix(vec3(l), color, saturation);
      color = (color - .5) * contrast + .5;
      color *= vec3(1. + warmth * .1, 1. + warmth * .02, 1. - warmth * .12);
      if (splitTone > 0.) {
        float lit = luma(color);
        color = mix(color, color * vec3(.84, .95, 1.14), splitTone * (1. - smoothstep(.12, .5, lit)));
        color = mix(color, color * vec3(1.1, 1.02, .84), splitTone * smoothstep(.4, .85, lit));
      }
    } else if (stage == 2) {
      if (grain > 0.) {
        float paper;
        if (grainWorld) {
          vec3 p = worldAt(uv);
          paper = noise3(p * 60.) * .5 + noise3(p * 14.) * .3 + noise3(p * vec3(3., 60., 3.)) * .1 + noise3(p * vec3(60., 3., 3.)) * .1;
        } else {
          // Fixed on the screen, in picture pixels: it does not move or scale with the camera.
          vec2 q = uv * size;
          paper = noise(q * .9) * .5 + noise(q * .23) * .3 + noise(q * vec2(.05, .6)) * .1 + noise(q * vec2(.6, .05)) * .1;
        }
        color *= 1. + (paper - .5) * grain * .6;
        color = mix(color, color * vec3(1.03, 1., .9), grain * .4);
      }
    } else if (stage == 3) {
      if (vignette > 0.) color *= mix(1., smoothstep(1.05, .3, length(vUv - .5) * 1.35), vignette);
    } else if (stage == 4) {
      if (paletteSize > 0) color = nearest(clamp(color + threshold * dither * .25, 0., 1.));
      else if (posterize > 1.) color = floor(clamp(color, 0., 1.) * (posterize - 1.) + .5 + threshold * dither) / (posterize - 1.);
    } else {
      if (scanlines > 0.) {
        float line = .5 + .5 * cos(gl_FragCoord.y * 2.0944);
        color *= 1. - scanlines * .45 * (1. - line);
        int slot = int(mod(gl_FragCoord.x, 3.));
        vec3 mask = slot == 0 ? vec3(1., .82, .82) : slot == 1 ? vec3(.82, 1., .82) : vec3(.82, .82, 1.);
        color *= mix(vec3(1.), mask, scanlines * .6) * (1. + scanlines * .18);
      }
    }
  }
  gl_FragColor = vec4(clamp(color, 0., 1.), 1.);
  // The world's depth stays on the canvas, so ground UI drawn crisp afterwards is still hidden behind figures and props.
  gl_FragDepth = texture2D(tDepth, uv).x;
}`;

/**
 * Draws the world into its own target and then onto the canvas through the filter shader. `begin` before the world render,
 * `finish` after it and before anything drawn over the world (hover outlines, the 3D HUD), which therefore stays unfiltered.
 */
export class PictureFilterPass {
  private target: THREE.WebGLRenderTarget | null = null;
  private screen = new THREE.Scene();
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private buffer = new THREE.Vector2();
  private paletteColors = Array.from({ length: MAX_COLORS }, () => new THREE.Vector3());
  private material = new THREE.ShaderMaterial({
    vertexShader, fragmentShader, depthTest: true, depthFunc: THREE.AlwaysDepth, depthWrite: true, toneMapped: false,
    uniforms: {
      tColor: { value: null }, tDepth: { value: null }, size: { value: new THREE.Vector2(1, 1) }, cameraNear: { value: 0 }, cameraFar: { value: 1 }, inverseViewProjection: { value: new THREE.Matrix4() },
      posterize: { value: 0 }, dither: { value: 0 }, ink: { value: 0 }, inkThickness: { value: 1 }, inkDepth: { value: .35 }, painterly: { value: 0 }, grain: { value: 0 },
      warmth: { value: 0 }, saturation: { value: 1 }, contrast: { value: 1 }, vignette: { value: 0 }, scanlines: { value: 0 }, curvature: { value: 0 }, aberration: { value: 0 },
      antialias: { value: true }, tilt: { value: 0 }, edgeFade: { value: 0 }, splitTone: { value: 0 }, inkWeight: { value: 0 }, compare: { value: false }, grainWorld: { value: true }, compareAt: { value: .5 },
      stageOrder: { value: [0, 1, 2, 3, 4, 5] }, paletteSize: { value: 0 }, palette: { value: this.paletteColors }, inkColor: { value: new THREE.Vector3(.1, .07, .05) },
    },
  });
  private gl: WebGL2RenderingContext;
  private timer: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  private queries: WebGLQuery[] = [];

  constructor(private renderer: THREE.WebGLRenderer) {
    const geometry = new THREE.BufferGeometry();
    // One triangle over the whole screen: no diagonal seam, fewer fragments than a quad's two.
    geometry.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2));
    const quad = new THREE.Mesh(geometry, this.material); quad.frustumCulled = false;
    this.screen.add(quad);
    this.gl = renderer.getContext() as WebGL2RenderingContext;
    this.timer = this.gl.getExtension('EXT_disjoint_timer_query_webgl2');
  }

  /**
   * Points the next world render at the filter's target. The target poses as the screen (three's XR flag): the world draws with
   * the same tone mapping and output encoding as on the canvas, so it reuses the canvas's shader programs instead of compiling a
   * second set, which stalled the page for over a second the first time the filters came on.
   */
  begin(camera: THREE.OrthographicCamera, filters: Readonly<PictureFilters> = PICTURE_FILTERS) {
    this.resize(withZoom(filters, camera.zoom));
    const uniforms = this.material.uniforms;
    uniforms.cameraNear.value = camera.near; uniforms.cameraFar.value = camera.far;
    this.view = camera;
    this.renderer.setRenderTarget(this.target);
  }

  private view: THREE.OrthographicCamera | null = null;
  private inverse = new THREE.Matrix4();

  private resize(filters: Readonly<PictureFilters>) {
    this.renderer.getDrawingBufferSize(this.buffer);
    const pixel = Math.max(1, filters.pixelSize) * (filters.pixelSize > 1 ? this.renderer.getPixelRatio() : 1);
    const width = Math.max(1, Math.round(this.buffer.x / pixel)), height = Math.max(1, Math.round(this.buffer.y / pixel));
    // No multisampling: distortion shells copy the frame drawn so far, which a multisampled target cannot give them.
    // At full size the filter shader antialiases instead (FXAA); big pixels stay hard.
    const filter = filters.pixelSize > 1 ? THREE.NearestFilter : THREE.LinearFilter;
    if (!this.target || this.target.width !== width || this.target.height !== height) {
      this.target?.dispose();
      // Half float keeps the display-encoded picture free of banding through the grade; values stay within 0–1.
      const depth = new THREE.DepthTexture(width, height, THREE.UnsignedInt248Type); depth.format = THREE.DepthStencilFormat;
      this.target = new THREE.WebGLRenderTarget(width, height, { type: THREE.HalfFloatType, colorSpace: this.renderer.outputColorSpace as THREE.ColorSpace,
        stencilBuffer: true, depthTexture: depth });
      Object.assign(this.target, { isXRRenderTarget: true });
      this.target.depthTexture!.minFilter = this.target.depthTexture!.magFilter = THREE.NearestFilter;
    }
    if (this.target.texture.magFilter !== filter) { this.target.texture.minFilter = this.target.texture.magFilter = filter; this.target.texture.needsUpdate = true; }
  }

  finish(chosen: Readonly<PictureFilters> = PICTURE_FILTERS) {
    const target = this.target; if (!target) return;
    const filters = withClock(withZoom(chosen, this.view?.zoom ?? 1));
    this.material.uniforms.grainWorld.value = chosen.follows.includes('grain');
    const uniforms = this.material.uniforms;
    // The world render has just refreshed the camera's matrices.
    if (this.view && filters.grain > 0) uniforms.inverseViewProjection.value.copy(this.inverse.multiplyMatrices(this.view.projectionMatrix, this.view.matrixWorldInverse).invert());
    uniforms.antialias.value = filters.pixelSize <= 1; uniforms.tColor.value = target.texture; uniforms.tDepth.value = target.depthTexture; uniforms.size.value.set(target.width, target.height);
    for (const key of ['posterize', 'dither', 'ink', 'inkThickness', 'inkDepth', 'painterly', 'grain', 'warmth', 'saturation', 'contrast', 'vignette', 'scanlines', 'curvature', 'aberration', 'tilt', 'edgeFade', 'splitTone', 'inkWeight', 'compareAt'] as const)
      uniforms[key].value = filters[key];
    uniforms.compare.value = filters.compare;
    filters.order.forEach((stage, i) => { uniforms.stageOrder.value[i] = STAGES.indexOf(stage); });
    const colors = filters.palette ? PALETTES[filters.palette].colors : [];
    uniforms.paletteSize.value = colors.length;
    colors.forEach((hex, i) => this.paletteColors[i].set((hex >> 16 & 255) / 255, (hex >> 8 & 255) / 255, (hex & 255) / 255));
    this.renderer.setRenderTarget(null);
    const timing = this.startTiming();
    this.renderer.render(this.screen, this.camera);
    if (timing) this.gl.endQuery(this.timer!.TIME_ELAPSED_EXT);
    pictureFilterStats.width = target.width; pictureFilterStats.height = target.height;
  }

  /** Times the filter draw without stalling: results are read a few frames later. Skipped while another timer (a frame profile) runs. */
  private startTiming() {
    const gl = this.gl, timer = this.timer;
    if (!timer) return false;
    while (this.queries.length && gl.getQueryParameter(this.queries[0], gl.QUERY_RESULT_AVAILABLE)) {
      const query = this.queries.shift()!;
      if (!gl.getParameter(timer.GPU_DISJOINT_EXT)) {
        const ms = gl.getQueryParameter(query, gl.QUERY_RESULT) / 1e6;
        pictureFilterStats.gpuMs = pictureFilterStats.gpuMs === null ? ms : pictureFilterStats.gpuMs * .9 + ms * .1;
      }
      gl.deleteQuery(query);
    }
    if (this.queries.length >= 4 || gl.getQuery(timer.TIME_ELAPSED_EXT, gl.CURRENT_QUERY)) return false;
    const query = gl.createQuery(); if (!query) return false;
    gl.beginQuery(timer.TIME_ELAPSED_EXT, query); this.queries.push(query);
    return true;
  }

  dispose() {
    this.target?.dispose(); this.target = null; this.material.dispose();
    for (const object of this.screen.children) (object as THREE.Mesh).geometry.dispose();
    for (const query of this.queries) this.gl.deleteQuery(query); this.queries.length = 0;
  }
}
